import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import UploadPage from './components/UploadPage';
import ProjectsPage from './components/ProjectsPage';
import ProjectDetailsPage from './components/ProjectDetailsPage';
import DashboardPage from './components/DashboardPage';
import PasswordAuth from './components/PasswordAuth';
import ValidationOverview from './components/validation/ValidationOverview';
import SettingsPage from './components/SettingsPage';
import { checkHealth } from './services/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const AppContent: React.FC = () => {
  const { isAuthenticated, login } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Check if backend is healthy
        const health = await checkHealth();
        console.log('Backend health:', health);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Continue anyway - user can still use the app
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Show password authentication if not authenticated
  if (!isAuthenticated) {
    return <PasswordAuth onAuthenticated={login} />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing VWBA Evaluator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gray-50 flex flex-col">
      <Navbar activeTab={activeTab} onTabChange={setActiveTab} />
      
      <main className="flex-1 overflow-auto w-full">
        {selectedProjectId ? (
          <ProjectDetailsPage 
            projectId={selectedProjectId} 
            onBack={() => setSelectedProjectId(null)} 
          />
        ) : (
          <>
            {activeTab === 'overview' && <ValidationOverview />}
            {activeTab === 'upload' && <UploadPage />}
            {activeTab === 'projects' && <ProjectsPage onViewDetails={setSelectedProjectId} onNavigateToUpload={() => setActiveTab('upload')} />}
            {activeTab === 'analytics' && (
              <DashboardPage onViewProject={(id) => {
                setSelectedProjectId(id);
              }} />
            )}
            {activeTab === 'settings' && <SettingsPage />}
          </>
        )}
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
